// src/components/infografia/canvas/InfografiaCanvas.jsx
import React, { useRef, useEffect, useState } from 'react';
import { Canvas, Text, Rect, Circle, Ellipse, Polygon, Triangle, Image as FabricImage, Gradient, Path } from 'fabric';
import QRCode from 'qrcode';
import { reloadCanvasImagesWithProxy } from '../../../helpers/infografia/imageProxyHelper';
import {
    generateRegularPolygon,
    generateStar,
    generateDiamond,
    generateParallelogram,
    generateTrapezoid,
    generateEquilateralTriangle
} from '../../../helpers/infografia/shapeGenerator';

const InfografiaCanvas = ({ data, marca, onChange, onElementSelect, selectedElement, onZoomChange, externalZoom, onCanvasReady }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [fabricCanvas, setFabricCanvas] = useState(null);
    const [zoom, setZoom] = useState(0.3);
    const isRegeneratingRef = useRef(false);
    const isAddingElementRef = useRef(false);
    const dataRef = useRef(data); // Mantener referencia actualizada de data

    // Actualizar dataRef cuando data cambie
    useEffect(() => {
        dataRef.current = data;
    }, [data]);

    // Si hay un zoom externo, usarlo
    const currentZoom = externalZoom !== undefined ? externalZoom : zoom;

    // Calcular zoom para que el canvas quepa en la pantalla pero sea lo más grande posible
    useEffect(() => {
        if (!containerRef.current || !data) return;

        // Si hay zoom externo, no calcular automáticamente
        if (externalZoom !== undefined) return;

        const updateZoom = () => {
            const container = containerRef.current;
            if (!container) return;

            const containerWidth = container.clientWidth - 40; // Margen mínimo
            const containerHeight = container.clientHeight - 40;

            const canvasWidth = data.dimensiones.ancho;
            const canvasHeight = data.dimensiones.alto;

            const scaleX = containerWidth / canvasWidth;
            const scaleY = containerHeight / canvasHeight;
            // Usar el menor para que quepa completo
            const newZoom = Math.min(scaleX, scaleY, 1); // Máximo 1 (100%)

            setZoom(newZoom);
            if (onZoomChange) {
                onZoomChange(newZoom);
            }
        };

        updateZoom();

        // Recalcular zoom cuando cambia el tamaño de la ventana
        const handleResize = () => {
            updateZoom();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [data?.dimensiones, externalZoom]);

    // Crear canvas inicial (solo cuando cambian dimensiones o se crea por primera vez)
    useEffect(() => {
        if (!canvasRef.current || !data) return;

        // Si ya existe un canvas y las dimensiones no han cambiado, no recrearlo
        if (fabricCanvas &&
            fabricCanvas.width === data.dimensiones.ancho &&
            fabricCanvas.height === data.dimensiones.alto) {
            return;
        }

        // Si las dimensiones cambiaron, destruir el canvas anterior
        if (fabricCanvas) {
            fabricCanvas.dispose();
            setFabricCanvas(null);
            // Esperar un momento para que se limpie completamente
            setTimeout(() => {
                // La creación del nuevo canvas se hará en la siguiente ejecución del useEffect
            }, 50);
            return;
        }

        // Usar dimensiones reales del canvas (sin zoom)
        // El zoom se aplicará con CSS transform
        const displayWidth = data.dimensiones.ancho;
        const displayHeight = data.dimensiones.alto;

        // Calcular fondo según el tipo
        let backgroundColor = '#FFFFFF';
        if (data.fondo.tipo === 'color') {
            backgroundColor = typeof data.fondo.valor === 'string'
                ? data.fondo.valor
                : data.fondo.valor?.color || '#FFFFFF';
        }

        // Calcular dimensiones escaladas con el zoom actual
        const scaledWidth = displayWidth * currentZoom;
        const scaledHeight = displayHeight * currentZoom;

        // Inicializar canvas de Fabric.js con tamaño escalado
        const canvas = new Canvas(canvasRef.current, {
            width: scaledWidth,
            height: scaledHeight,
            backgroundColor: backgroundColor,
            enableRetinaScaling: true
        });

        // Aplicar zoom inicial para que los elementos se escalen correctamente
        canvas.setZoom(currentZoom);

        setFabricCanvas(canvas);

        // Función para preparar el canvas para exportación (recargar imágenes con proxy CORS)
        const prepareForExport = async () => {
            if (!canvas) return;
            await reloadCanvasImagesWithProxy(canvas, (props) => props.key);
        };

        // Notificar al componente padre que el canvas está listo (pasar canvas y función de preparación)
        if (onCanvasReady) {
            onCanvasReady({
                canvas: canvas,
                prepareForExport: prepareForExport
            });
        }

        // Limpiar canvas antes de cargar elementos
        canvas.clear();
        canvas.backgroundColor = backgroundColor;

        // Si hay gradiente, crear un rectángulo con gradiente
        if (data.fondo.tipo === 'gradiente' && data.fondo.valor) {
            const grad = data.fondo.valor;
            const gradient = new Gradient({
                type: 'linear',
                coords: {
                    x1: 0,
                    y1: 0,
                    x2: displayWidth,
                    y2: displayHeight
                },
                colorStops: grad.colores.map((c, index) => ({
                    offset: c.posicion / 100,
                    color: c.color
                }))
            });

            // Ajustar dirección del gradiente según la dirección especificada
            const angle = parseInt(grad.direccion) || 135;
            const radians = (angle * Math.PI) / 180;
            const centerX = displayWidth / 2;
            const centerY = displayHeight / 2;
            const length = Math.sqrt(displayWidth * displayWidth + displayHeight * displayHeight) / 2;

            gradient.coords = {
                x1: centerX - Math.cos(radians) * length,
                y1: centerY - Math.sin(radians) * length,
                x2: centerX + Math.cos(radians) * length,
                y2: centerY + Math.sin(radians) * length
            };

            const bgRect = new Rect({
                left: 0,
                top: 0,
                width: displayWidth,
                height: displayHeight,
                fill: gradient,
                selectable: false,
                evented: false,
                isBackgroundGradient: true
            });
            canvas.add(bgRect);
            // En Fabric.js v6, usar moveTo en lugar de sendToBack
            canvas.sendObjectToBack(bgRect);
        }

        // Si hay imagen de fondo, cargarla
        if (data.fondo.tipo === 'imagen' && (data.fondo.valor?.cdnUrl || data.fondo.valor?.url)) {
            FabricImage.fromURL(data.fondo.valor.cdnUrl || data.fondo.valor.url, {
                crossOrigin: 'anonymous'
            }).then((img) => {
                const modo = data.fondo.valor.modo || 'cover';
                let scaleX, scaleY, left = 0, top = 0;

                if (modo === 'cover') {
                    const scale = Math.max(displayWidth / img.width, displayHeight / img.height);
                    scaleX = scale;
                    scaleY = scale;
                    left = (displayWidth - img.width * scale) / 2;
                    top = (displayHeight - img.height * scale) / 2;
                } else if (modo === 'contain') {
                    const scale = Math.min(displayWidth / img.width, displayHeight / img.height);
                    scaleX = scale;
                    scaleY = scale;
                    left = (displayWidth - img.width * scale) / 2;
                    top = (displayHeight - img.height * scale) / 2;
                } else { // repeat
                    scaleX = 1;
                    scaleY = 1;
                }

                img.set({
                    left,
                    top,
                    scaleX,
                    scaleY,
                    opacity: data.fondo.valor.opacidad || 1,
                    selectable: false,
                    evented: false
                });
                canvas.add(img);
                canvas.sendObjectToBack(img);
                canvas.renderAll();
            }).catch((error) => {
                console.error('Error cargando imagen de fondo:', error);
            });
        }

        // El zoom se aplica en el JSX con inline styles

        // NO cargar elementos aquí - se cargan en el useEffect separado
        // Esto evita duplicados cuando cambian dimensiones o zoom

        // Event listeners
        const handleSelection = (e) => {
            const obj = e.selected?.[0] || e.target;
            if (obj && obj.elementId) {
                // Buscar el elemento en el estado actual (data.elementos)
                let element = data.elementos.find(el => el.id === obj.elementId);

                // Si no se encuentra, intentar crear un elemento temporal desde el objeto del canvas
                if (!element && obj.elementType && obj.elementProperties) {
                    // Obtener todos los elementos del canvas para asegurarnos de no perder ninguno
                    const allCanvasObjects = canvas.getObjects().filter(o => o.elementId && !o.isBackgroundImage && !o.isBackgroundGradient);
                    const canvasElementIds = new Set(allCanvasObjects.map(o => o.elementId));

                    // Crear el elemento temporal
                    element = {
                        id: obj.elementId,
                        tipo: obj.elementType,
                        posicion: { x: obj.left || 0, y: obj.top || 0 },
                        dimensiones: {
                            ancho: (obj.width || 0) * (obj.scaleX || 1),
                            alto: (obj.height || 0) * (obj.scaleY || 1)
                        },
                        rotacion: obj.angle || 0,
                        zIndex: obj.zIndex || 0,
                        propiedades: obj.elementProperties || {}
                    };

                    // Crear un mapa de elementos existentes en data.elementos
                    const existingElementsMap = new Map(data.elementos.map(el => [el.id, el]));

                    // Añadir todos los elementos del canvas que no están en data.elementos
                    const missingElements = [];
                    allCanvasObjects.forEach(canvasObj => {
                        if (!existingElementsMap.has(canvasObj.elementId) && canvasObj.elementProperties) {
                            missingElements.push({
                                id: canvasObj.elementId,
                                tipo: canvasObj.elementType,
                                posicion: { x: canvasObj.left || 0, y: canvasObj.top || 0 },
                                dimensiones: {
                                    ancho: (canvasObj.width || 0) * (canvasObj.scaleX || 1),
                                    alto: (canvasObj.height || 0) * (canvasObj.scaleY || 1)
                                },
                                rotacion: canvasObj.angle || 0,
                                zIndex: canvasObj.zIndex || 0,
                                propiedades: canvasObj.elementProperties || {}
                            });
                        }
                    });

                    // Marcar que estamos añadiendo elementos para evitar que handleModified se ejecute
                    isAddingElementRef.current = true;

                    // Añadir todos los elementos faltantes a data.elementos
                    // IMPORTANTE: Mantener todos los elementos existentes y añadir los faltantes
                    const updatedData = {
                        ...data,
                        elementos: [...data.elementos, ...missingElements]
                    };
                    onChange(updatedData);

                    // Rehabilitar handleModified después de un breve delay
                    setTimeout(() => {
                        isAddingElementRef.current = false;
                    }, 500);
                }

                if (element) {
                    // Usar las propiedades del objeto de Fabric.js como fuente de verdad
                    // El canvas es la fuente de verdad visual, así que priorizamos obj.elementProperties
                    const finalProps = obj.elementProperties && Object.keys(obj.elementProperties).length > 0
                        ? obj.elementProperties
                        : element.propiedades;

                    const elementWithCanvasProps = {
                        ...element,
                        propiedades: {
                            ...element.propiedades,
                            ...finalProps
                        }
                    };
                    onElementSelect(elementWithCanvasProps);
                }
            }
        };

        // Flag para evitar actualizaciones durante eliminación o regeneración
        const isDeletingRef = { current: false };
        const lastElementCountRef = { current: data.elementos.length };

        const handleModified = () => {
            // Si estamos eliminando o regenerando, no actualizar
            if (isDeletingRef.current || isRegeneratingRef.current) {
                return;
            }

            // Usar dataRef.current para obtener la versión más actualizada
            const currentData = dataRef.current;

            // Obtener todos los objetos del canvas
            const canvasObjects = canvas.getObjects().filter(obj => obj.elementId && !obj.isBackgroundImage && !obj.isBackgroundGradient);

            // Actualizar contador
            lastElementCountRef.current = canvasObjects.length;

            // Filtrar objetos duplicados: solo mantener el último objeto con cada elementId
            const uniqueObjects = [];
            const seenIds = new Set();
            for (let i = canvasObjects.length - 1; i >= 0; i--) {
                const obj = canvasObjects[i];
                if (obj.elementId && !seenIds.has(obj.elementId)) {
                    uniqueObjects.unshift(obj);
                    seenIds.add(obj.elementId);
                }
            }

            const elementosActualizados = uniqueObjects.map(obj => {
                // Buscar el elemento original en currentData.elementos para preservar sus propiedades
                const originalElement = currentData.elementos.find(el => el.id === obj.elementId);

                if (!originalElement) {
                    // Si no existe el elemento original, NO crear uno nuevo aquí
                    // Los elementos nuevos deben añadirse desde el toolbar, no desde handleModified
                    console.warn(`⚠️ Elemento ${obj.elementId} no encontrado en data.elementos, ignorando`);
                    return null;
                }

                // Para elementos QR e iconos, siempre usar las propiedades del elemento original
                // ya que las propiedades se actualizan desde el panel de propiedades
                // y no desde el canvas
                const useOriginalProps = (obj.elementType === 'qr' || obj.elementType === 'icono') && originalElement;

                const baseElement = {
                    id: obj.elementId,
                    tipo: obj.elementType,
                    // Convertir coordenadas de vuelta al tamaño real (dividir por zoom)
                    // Las posiciones en el canvas son reales (sin zoom), ya que el zoom es CSS
                    posicion: { x: obj.left, y: obj.top },
                    rotacion: obj.angle || 0,
                    zIndex: obj.zIndex || 0,
                    // Para QR, preservar propiedades originales. Para otros elementos, usar las del canvas
                    propiedades: useOriginalProps
                        ? { ...originalElement.propiedades }
                        : (obj.elementProperties && Object.keys(obj.elementProperties).length > 0
                            ? { ...obj.elementProperties }
                            : (originalElement?.propiedades || {}))
                };

                // Calcular dimensiones según el tipo (convertir de vuelta al tamaño real)
                if (obj.type === 'circle') {
                    // Las dimensiones son reales (sin zoom), ya que el zoom es CSS
                    const radius = obj.radius * obj.scaleX;
                    baseElement.dimensiones = { ancho: radius * 2, alto: radius * 2 };
                } else if (obj.type === 'ellipse') {
                    baseElement.dimensiones = {
                        ancho: (obj.rx || 0) * 2 * (obj.scaleX || 1),
                        alto: (obj.ry || 0) * 2 * (obj.scaleY || 1)
                    };
                } else if (obj.type === 'triangle' || obj.type === 'polygon' || obj.type === 'path') {
                    baseElement.dimensiones = {
                        ancho: (obj.width || 0) * (obj.scaleX || 1),
                        alto: (obj.height || 0) * (obj.scaleY || 1)
                    };
                } else {
                    baseElement.dimensiones = {
                        ancho: (obj.width || 0) * (obj.scaleX || 1),
                        alto: (obj.height || 0) * (obj.scaleY || 1)
                    };
                }

                return baseElement;
            });

            // Verificar que todos los elementos tienen IDs válidos
            // Filtrar elementos válidos (que tengan id y no sean null)
            const validElements = elementosActualizados.filter(el => el && el.id);

            // Actualizar contador
            lastElementCountRef.current = validElements.length;

            // Actualizar siempre - los elementos nuevos se añadirán automáticamente
            onChange({
                ...currentData,
                elementos: validElements
            });
        };

        canvas.on('selection:created', handleSelection);
        canvas.on('selection:updated', handleSelection);
        canvas.on('mouse:down', (e) => {
            if (e.target && e.target.elementId) {
                handleSelection({ target: e.target });
            } else {
                onElementSelect(null);
            }
        });
        // Deshabilitar temporalmente handleModified durante eliminación
        let handleModifiedEnabled = true;

        const wrappedHandleModified = () => {
            if (!handleModifiedEnabled || isDeletingRef.current) {
                return;
            }
            handleModified();
        };

        // Event listener para controlar límites de escalado
        canvas.on('object:scaling', (e) => {
            const obj = e.target;
            if (!obj || !obj.elementType) return;

            // Definir límites según el tipo de elemento
            let minWidth = 10;
            let maxWidth = Infinity;
            let minHeight = 10;
            let maxHeight = Infinity;

            switch (obj.elementType) {
                case 'texto':
                    // Para texto, limitar el ancho (la altura se ajusta automáticamente)
                    minWidth = 50;
                    maxWidth = 2000; // Ancho máximo razonable
                    break;
                case 'qr':
                    // QR debe ser cuadrado y entre 100-1000px
                    minWidth = minHeight = 100;
                    maxWidth = maxHeight = 1000;
                    break;
                case 'imagen':
                    // Imágenes pueden ser de cualquier tamaño razonable
                    minWidth = minHeight = 50;
                    maxWidth = maxHeight = 5000;
                    break;
                case 'forma':
                case 'icono':
                    // Formas e iconos entre 20-2000px
                    minWidth = minHeight = 20;
                    maxWidth = maxHeight = 2000;
                    break;
            }

            // Calcular dimensiones actuales escaladas
            const currentWidth = (obj.width || 0) * (obj.scaleX || 1);
            const currentHeight = (obj.height || 0) * (obj.scaleY || 1);

            // Limitar escala si excede los límites
            if (currentWidth < minWidth) {
                obj.scaleX = minWidth / (obj.width || 1);
            } else if (currentWidth > maxWidth) {
                obj.scaleX = maxWidth / (obj.width || 1);
            }

            if (currentHeight < minHeight) {
                obj.scaleY = minHeight / (obj.height || 1);
            } else if (currentHeight > maxHeight) {
                obj.scaleY = maxHeight / (obj.height || 1);
            }

            // Para QR, mantener proporción cuadrada
            if (obj.elementType === 'qr') {
                const avgScale = (obj.scaleX + obj.scaleY) / 2;
                obj.scaleX = obj.scaleY = avgScale;
            }
        });

        canvas.on('object:modified', wrappedHandleModified);
        canvas.on('object:moved', wrappedHandleModified);
        canvas.on('object:scaled', wrappedHandleModified);
        canvas.on('object:rotated', wrappedHandleModified);

        // Manejar eliminación con tecla Delete
        const handleKeyDown = (e) => {
            if ((e.key === 'Delete' || e.key === 'Backspace') && canvas.getActiveObject()) {
                const activeObject = canvas.getActiveObject();
                if (activeObject.elementId) {
                    isDeletingRef.current = true;

                    const elementIdToDelete = activeObject.elementId;

                    // Eliminar TODOS los objetos con el mismo elementId del canvas (por si hay duplicados)
                    const objectsToRemove = canvas.getObjects().filter(obj => obj.elementId === elementIdToDelete);
                    objectsToRemove.forEach(obj => {
                        canvas.remove(obj);
                    });
                    canvas.renderAll();

                    // Usar dataRef.current para obtener la versión MÁS ACTUALIZADA de data
                    const currentData = dataRef.current;

                    // Verificar si el elemento existe en data.elementos
                    const elementExists = currentData.elementos.some(el => el.id === elementIdToDelete);

                    const updatedElements = currentData.elementos.filter(el => el.id !== elementIdToDelete);

                    // Actualizar contador de elementos
                    lastElementCountRef.current = updatedElements.length;

                    // Deshabilitar handleModified temporalmente
                    handleModifiedEnabled = false;
                    isDeletingRef.current = true;

                    onElementSelect(null);

                    // Actualizar estado inmediatamente usando currentData
                    onChange({
                        ...currentData,
                        elementos: updatedElements
                    });

                    // Rehabilitar handleModified después de un breve delay
                    setTimeout(() => {
                        isDeletingRef.current = false;
                        handleModifiedEnabled = true;
                    }, 300);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            canvas.off('selection:created', handleSelection);
            canvas.off('selection:updated', handleSelection);
            canvas.off('mouse:down');
            canvas.off('object:scaling');
            canvas.off('object:modified', wrappedHandleModified);
            canvas.off('object:moved', wrappedHandleModified);
            canvas.off('object:scaled', wrappedHandleModified);
            canvas.off('object:rotated', wrappedHandleModified);
            window.removeEventListener('keydown', handleKeyDown);
            canvas.dispose();
            setFabricCanvas(null); // Reset para permitir recreación
        };
    }, [data.dimensiones.ancho, data.dimensiones.alto]);

    // Actualizar zoom del canvas cuando cambie
    useEffect(() => {
        if (!fabricCanvas || !data) return;

        // Calcular dimensiones escaladas
        const scaledWidth = data.dimensiones.ancho * currentZoom;
        const scaledHeight = data.dimensiones.alto * currentZoom;

        // Aplicar zoom a Fabric.js
        fabricCanvas.setZoom(currentZoom);

        // Ajustar el tamaño del canvas en el DOM para que coincida con el zoom
        fabricCanvas.setWidth(scaledWidth);
        fabricCanvas.setHeight(scaledHeight);

        fabricCanvas.renderAll();
    }, [fabricCanvas, currentZoom, data?.dimensiones]);

    // Sincronizar elementos: añadir nuevos y eliminar los que ya no existen
    useEffect(() => {
        if (!fabricCanvas || !data || currentZoom === 0) return;

        // Usar una referencia a data.elementos para evitar dependencias circulares
        const elementosData = data.elementos;

        const existingObjects = fabricCanvas.getObjects().filter(obj =>
            obj.elementId && !obj.isBackgroundImage && !obj.isBackgroundGradient
        );

        // Limpiar duplicados: mantener solo el último objeto con cada elementId
        const uniqueObjects = [];
        const seenIds = new Set();
        const duplicatesToRemove = [];

        for (let i = existingObjects.length - 1; i >= 0; i--) {
            const obj = existingObjects[i];
            if (obj.elementId && !seenIds.has(obj.elementId)) {
                uniqueObjects.unshift(obj);
                seenIds.add(obj.elementId);
            } else if (obj.elementId) {
                // Es un duplicado, marcarlo para eliminación
                duplicatesToRemove.push(obj);
            }
        }

        // Eliminar duplicados del canvas
        if (duplicatesToRemove.length > 0) {
            duplicatesToRemove.forEach(obj => fabricCanvas.remove(obj));
            fabricCanvas.renderAll();
        }

        const existingIds = uniqueObjects.map(obj => obj.elementId);
        const currentElementIds = elementosData.map(el => el.id);

        // Eliminar elementos que ya no están en data.elementos
        // PERO solo si no estamos añadiendo elementos (para evitar eliminar elementos que se están añadiendo)
        const toRemove = uniqueObjects.filter(obj => !currentElementIds.includes(obj.elementId));

        if (toRemove.length > 0 && !isAddingElementRef.current) {
            toRemove.forEach(obj => {
                fabricCanvas.remove(obj);
            });
            fabricCanvas.renderAll();
        }

        // Encontrar elementos nuevos que no están en el canvas
        // IMPORTANTE: Solo añadir elementos que realmente están en data.elementos
        const newElements = elementosData.filter(el => !existingIds.includes(el.id));

        if (newElements.length > 0) {
            loadElements(fabricCanvas, newElements);
            // Forzar renderizado después de cargar
            setTimeout(() => {
                fabricCanvas.renderAll();

                // Si hay un elemento seleccionado y es uno de los nuevos, seleccionarlo en el canvas
                if (selectedElement && newElements.some(el => el.id === selectedElement.id)) {
                    const obj = fabricCanvas.getObjects().find(o => o.elementId === selectedElement.id);
                    if (obj) {
                        fabricCanvas.setActiveObject(obj);
                        fabricCanvas.renderAll();
                    }
                }
            }, 100);
        }

        // Sincronizar zIndex - reordenar objetos en el canvas según el orden en data.elementos
        const allObjects = fabricCanvas.getObjects().filter(obj =>
            obj.elementId && !obj.isBackgroundImage && !obj.isBackgroundGradient
        );

        if (allObjects.length > 0) {
            // Crear un mapa de elementId -> índice en data.elementos
            const elementIndexMap = new Map();
            elementosData.forEach((el, index) => {
                elementIndexMap.set(el.id, index);
            });

            // Ordenar los objetos según su posición en data.elementos
            const sortedObjects = [...allObjects].sort((a, b) => {
                const indexA = elementIndexMap.get(a.elementId) ?? 999;
                const indexB = elementIndexMap.get(b.elementId) ?? 999;
                return indexA - indexB;
            });

            // Remover todos los objetos
            allObjects.forEach(obj => fabricCanvas.remove(obj));

            // Añadirlos de nuevo en el orden correcto
            sortedObjects.forEach(obj => fabricCanvas.add(obj));

            fabricCanvas.renderAll();
        }
    }, [data.elementos, fabricCanvas, currentZoom]);

    // Actualizar elemento en canvas cuando cambian sus propiedades
    useEffect(() => {
        if (!fabricCanvas || !selectedElement) return;

        const obj = fabricCanvas.getObjects().find(o => o.elementId === selectedElement.id);
        if (!obj) return;

        // Actualizar propiedades según el tipo
        if (obj.elementType === 'texto') {
            // Actualizar elementProperties para evitar que handleModified cree duplicados
            obj.elementProperties = { ...selectedElement.propiedades };

            if (obj.text !== selectedElement.propiedades.contenido) {
                obj.set('text', selectedElement.propiedades.contenido || 'Texto');
            }
            // Tamaños son reales (sin zoom), ya que el zoom es CSS
            if (obj.fontSize !== (selectedElement.propiedades.tamaño || 24)) {
                obj.set('fontSize', selectedElement.propiedades.tamaño || 24);
            }
            if (obj.fill !== selectedElement.propiedades.color) {
                obj.set('fill', selectedElement.propiedades.color || '#000000');
            }
            if (obj.fontFamily !== selectedElement.propiedades.fuente) {
                obj.set('fontFamily', selectedElement.propiedades.fuente || 'Arial');
            }
            if (obj.fontWeight !== (selectedElement.propiedades.negrita ? 'bold' : 'normal')) {
                obj.set('fontWeight', selectedElement.propiedades.negrita ? 'bold' : 'normal');
            }
            if (obj.fontStyle !== (selectedElement.propiedades.cursiva ? 'italic' : 'normal')) {
                obj.set('fontStyle', selectedElement.propiedades.cursiva ? 'italic' : 'normal');
            }
            if (obj.textAlign !== selectedElement.propiedades.alineacion) {
                obj.set('textAlign', selectedElement.propiedades.alineacion || 'left');
            }
        } else if (obj.elementType === 'imagen') {
            if (obj.opacity !== selectedElement.propiedades.opacidad) {
                obj.set('opacity', selectedElement.propiedades.opacidad || 1);
            }
            // Si cambió la URL, recargar imagen
            const newImageUrl = selectedElement.propiedades.cdnUrl || selectedElement.propiedades.url;
            const currentImageUrl = obj.elementProperties?.cdnUrl || obj.elementProperties?.url;

            if (newImageUrl && newImageUrl !== currentImageUrl) {
                isRegeneratingRef.current = true;

                // Eliminar imagen antigua y cualquier placeholder
                const objectsToRemove = fabricCanvas.getObjects().filter(o => o.elementId === selectedElement.id);
                objectsToRemove.forEach(o => fabricCanvas.remove(o));

                // Intentar primero sin crossOrigin (para que se vea)
                FabricImage.fromURL(newImageUrl).then((img) => {
                    // Usar dimensiones reales (sin zoom), ya que el zoom es CSS
                    const scaledWidth = selectedElement.dimensiones.ancho;
                    const scaledHeight = selectedElement.dimensiones.alto;
                    img.set({
                        left: selectedElement.posicion.x,
                        top: selectedElement.posicion.y,
                        scaleX: scaledWidth / img.width,
                        scaleY: scaledHeight / img.height,
                        selectable: true,
                        hasControls: true,
                        hasBorders: true,
                        opacity: selectedElement.propiedades.opacidad || 1
                    });
                    img.elementId = selectedElement.id;
                    img.elementType = 'imagen';
                    img.elementProperties = selectedElement.propiedades;
                    img.hasCORS = false; // Se usará el proxy al exportar
                    fabricCanvas.add(img);
                    fabricCanvas.renderAll();

                    setTimeout(() => {
                        isRegeneratingRef.current = false;
                    }, 100);

                    // Nota: No intentamos cargar con CORS aquí, el proxy se encarga al exportar
                })
                    .catch((error) => {
                        // Intentar con crossOrigin como último recurso
                        FabricImage.fromURL(newImageUrl, {
                            crossOrigin: 'anonymous'
                        }).then((img) => {
                            const scaledWidth = selectedElement.dimensiones.ancho;
                            const scaledHeight = selectedElement.dimensiones.alto;
                            img.set({
                                left: selectedElement.posicion.x,
                                top: selectedElement.posicion.y,
                                scaleX: scaledWidth / img.width,
                                scaleY: scaledHeight / img.height,
                                selectable: true,
                                hasControls: true,
                                hasBorders: true,
                                opacity: selectedElement.propiedades.opacidad || 1
                            });
                            img.elementId = selectedElement.id;
                            img.elementType = 'imagen';
                            img.elementProperties = selectedElement.propiedades;
                            img.hasCORS = true;
                            fabricCanvas.add(img);
                            fabricCanvas.renderAll();

                            setTimeout(() => {
                                isRegeneratingRef.current = false;
                            }, 100);
                        })
                            .catch((error2) => {
                                isRegeneratingRef.current = false;

                                // Crear placeholder si falla
                                const placeholder = new Rect({
                                    left: selectedElement.posicion.x,
                                    top: selectedElement.posicion.y,
                                    width: selectedElement.dimensiones.ancho,
                                    height: selectedElement.dimensiones.alto,
                                    fill: '#E5E7EB',
                                    stroke: '#9CA3AF',
                                    strokeWidth: 2,
                                    strokeDashArray: [5, 5],
                                    selectable: true,
                                    hasControls: true,
                                    hasBorders: true
                                });
                                placeholder.elementId = selectedElement.id;
                                placeholder.elementType = 'imagen';
                                placeholder.elementProperties = selectedElement.propiedades;
                                fabricCanvas.add(placeholder);
                                fabricCanvas.renderAll();
                            });
                    });
                return;
            }
        } else if (obj.elementType === 'qr') {
            // Si cambió la URL, color o tamaño, regenerar el QR
            const urlChanged = selectedElement.propiedades.url !== obj.elementProperties?.url;
            const colorChanged = selectedElement.propiedades.color !== obj.elementProperties?.color;
            const bgColorChanged = selectedElement.propiedades.colorFondo !== obj.elementProperties?.colorFondo;
            const sizeChanged = selectedElement.propiedades.tamaño !== obj.elementProperties?.tamaño;

            if (urlChanged || colorChanged || bgColorChanged || sizeChanged) {

                // Actualizar elementProperties inmediatamente para evitar ciclos
                obj.elementProperties = { ...selectedElement.propiedades };

                const qrUrl = selectedElement.propiedades.url || 'https://example.com';
                const qrColor = selectedElement.propiedades.color || '#000000';
                const qrBgColor = selectedElement.propiedades.colorFondo || '#FFFFFF';
                const qrSize = selectedElement.propiedades.tamaño || selectedElement.dimensiones.ancho || 300;

                QRCode.toDataURL(qrUrl, {
                    width: qrSize,
                    margin: 2,
                    color: {
                        dark: qrColor,
                        light: qrBgColor
                    },
                    errorCorrectionLevel: 'H'
                })
                    .then((dataUrl) => {
                        FabricImage.fromURL(dataUrl, {
                            crossOrigin: 'anonymous'
                        })
                            .then((img) => {
                                const scaledWidth = selectedElement.dimensiones.ancho;
                                const scaledHeight = selectedElement.dimensiones.alto;
                                const scaleX = scaledWidth / img.width;
                                const scaleY = scaledHeight / img.height;

                                img.set({
                                    left: selectedElement.posicion.x,
                                    top: selectedElement.posicion.y,
                                    scaleX: scaleX,
                                    scaleY: scaleY,
                                    selectable: true,
                                    hasControls: true,
                                    hasBorders: true
                                });
                                img.elementId = selectedElement.id;
                                img.elementType = 'qr';
                                img.elementProperties = { ...selectedElement.propiedades };
                                fabricCanvas.remove(obj);
                                fabricCanvas.add(img);
                                fabricCanvas.renderAll();
                            })
                            .catch((error) => {
                            });
                    })
                    .catch((error) => {
                    });
                return;
            }
        } else if (obj.elementType === 'icono') {
            // Si cambió el icono, color, tamaño u opacidad, regenerar el icono
            const iconChanged = selectedElement.propiedades.nombre !== obj.elementProperties?.nombre ||
                selectedElement.propiedades.libreria !== obj.elementProperties?.libreria;
            const colorChanged = selectedElement.propiedades.color !== obj.elementProperties?.color;
            const sizeChanged = selectedElement.propiedades.tamaño !== obj.elementProperties?.tamaño;
            // Comparar opacidad con tolerancia para evitar problemas de precisión de punto flotante
            const opacityChanged = Math.abs((selectedElement.propiedades.opacidad || 1) - (obj.elementProperties?.opacidad || 1)) > 0.01;

            if (iconChanged || colorChanged || sizeChanged || opacityChanged) {

                // Marcar que estamos regenerando para evitar que handleModified se ejecute
                isRegeneratingRef.current = true;

                // Actualizar elementProperties inmediatamente
                obj.elementProperties = { ...selectedElement.propiedades };

                const iconProps = selectedElement.propiedades;
                const iconName = iconProps.nombre;
                const iconLibrary = iconProps.libreria;
                const iconColor = iconProps.color || '#000000';
                // Usar el tamaño de las propiedades, o las dimensiones del elemento como fallback
                const iconSize = iconProps.tamaño || selectedElement.dimensiones?.ancho || 100;
                const iconOpacity = iconProps.opacidad !== undefined ? iconProps.opacidad : 1;

                if (iconName && iconLibrary) {
                    const tempDiv = document.createElement('div');
                    tempDiv.style.position = 'absolute';
                    tempDiv.style.left = '-9999px';
                    tempDiv.style.width = `${iconSize}px`;
                    tempDiv.style.height = `${iconSize}px`;
                    document.body.appendChild(tempDiv);

                    try {
                        let IconLib = null;
                        let IconComponent = null;

                        if (iconLibrary === 'Font Awesome') {
                            IconLib = require('react-icons/fa');
                        } else if (iconLibrary === 'Material Design') {
                            IconLib = require('react-icons/md');
                        } else if (iconLibrary === 'Heroicons') {
                            IconLib = require('react-icons/hi');
                        } else if (iconLibrary === 'Ionicons') {
                            IconLib = require('react-icons/io5');
                        }

                        if (IconLib && IconLib[iconName]) {
                            IconComponent = IconLib[iconName];
                        }

                        if (IconComponent) {
                            const React = require('react');
                            const ReactDOM = require('react-dom/client');
                            const root = ReactDOM.createRoot(tempDiv);

                            root.render(React.createElement(IconComponent, {
                                style: {
                                    width: `${iconSize}px`,
                                    height: `${iconSize}px`,
                                    color: iconColor,
                                    opacity: iconOpacity
                                }
                            }));

                            setTimeout(() => {
                                const svgElement = tempDiv.querySelector('svg');
                                if (svgElement) {
                                    svgElement.setAttribute('width', iconSize.toString());
                                    svgElement.setAttribute('height', iconSize.toString());
                                    svgElement.setAttribute('fill', iconColor);
                                    svgElement.style.opacity = iconOpacity.toString();

                                    const svgString = new XMLSerializer().serializeToString(svgElement);
                                    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
                                    const svgUrl = URL.createObjectURL(svgBlob);

                                    FabricImage.fromURL(svgUrl, {
                                        crossOrigin: 'anonymous'
                                    })
                                        .then((img) => {
                                            // Usar el tamaño de las propiedades o las dimensiones del elemento
                                            const targetWidth = iconSize;
                                            const targetHeight = iconSize;
                                            const scaleX = targetWidth / img.width;
                                            const scaleY = targetHeight / img.height;

                                            // Eliminar TODOS los objetos con el mismo elementId antes de añadir el nuevo
                                            const objectsToRemove = fabricCanvas.getObjects().filter(o => o.elementId === selectedElement.id);
                                            objectsToRemove.forEach(o => fabricCanvas.remove(o));

                                            img.set({
                                                left: selectedElement.posicion.x,
                                                top: selectedElement.posicion.y,
                                                scaleX: scaleX,
                                                scaleY: scaleY,
                                                opacity: iconOpacity,
                                                selectable: true,
                                                hasControls: true,
                                                hasBorders: true
                                            });
                                            img.elementId = selectedElement.id;
                                            img.elementType = 'icono';
                                            img.elementProperties = { ...selectedElement.propiedades };
                                            fabricCanvas.add(img);
                                            fabricCanvas.renderAll();
                                            URL.revokeObjectURL(svgUrl);
                                            document.body.removeChild(tempDiv);

                                            // Rehabilitar handleModified después de un breve delay
                                            setTimeout(() => {
                                                isRegeneratingRef.current = false;
                                            }, 300);

                                        })
                                        .catch((error) => {
                                            document.body.removeChild(tempDiv);
                                            isRegeneratingRef.current = false;
                                        });
                                } else {
                                    document.body.removeChild(tempDiv);
                                    isRegeneratingRef.current = false;
                                }
                            }, 200);
                            return;
                        } else {
                            document.body.removeChild(tempDiv);
                            isRegeneratingRef.current = false;
                        }
                    } catch (error) {
                        if (tempDiv.parentNode) {
                            document.body.removeChild(tempDiv);
                        }
                        isRegeneratingRef.current = false;
                    }
                } else {
                    isRegeneratingRef.current = false;
                }
            }
        } else if (obj.elementType === 'forma') {
            // Si el tipo de forma cambió, necesitamos regenerar el objeto
            const currentShapeType = obj.elementProperties?.tipo;
            const newShapeType = selectedElement.propiedades.tipo;

            if (currentShapeType !== newShapeType) {
                // Regenerar la forma con el nuevo tipo
                const elemento = data.elementos.find(el => el.id === selectedElement.id);
                if (elemento) {
                    fabricCanvas.remove(obj);
                    loadElements(fabricCanvas, [elemento]);
                    fabricCanvas.renderAll();
                }
                return;
            }

            // Actualizar propiedades existentes
            if (obj.fill !== selectedElement.propiedades.colorRelleno) {
                obj.set('fill', selectedElement.propiedades.colorRelleno || '#3B82F6');
            }
            if (obj.stroke !== selectedElement.propiedades.colorBorde) {
                obj.set('stroke', selectedElement.propiedades.colorBorde || '#000000');
            }
            if (obj.strokeWidth !== (selectedElement.propiedades.grosorBorde || 0)) {
                obj.set('strokeWidth', selectedElement.propiedades.grosorBorde || 0);
            }
            if (obj.opacity !== selectedElement.propiedades.opacidad) {
                obj.set('opacity', selectedElement.propiedades.opacidad || 1);
            }
        }

        // Actualizar propiedades guardadas en el objeto
        obj.elementProperties = selectedElement.propiedades;
        fabricCanvas.renderAll();
    }, [selectedElement, fabricCanvas, currentZoom]);

    // Actualizar fondo cuando cambia
    useEffect(() => {
        if (!fabricCanvas || !data) return;

        // Eliminar fondo anterior (gradiente o imagen)
        const existingBgGradient = fabricCanvas.getObjects().find(obj => obj.isBackgroundGradient);
        if (existingBgGradient) {
            fabricCanvas.remove(existingBgGradient);
        }
        const existingBgImage = fabricCanvas.getObjects().find(obj => obj.isBackgroundImage);
        if (existingBgImage) {
            fabricCanvas.remove(existingBgImage);
        }

        const displayWidth = data.dimensiones.ancho * currentZoom;
        const displayHeight = data.dimensiones.alto * currentZoom;

        if (data.fondo.tipo === 'color') {
            const bgColor = typeof data.fondo.valor === 'string'
                ? data.fondo.valor
                : data.fondo.valor?.color || '#FFFFFF';
            fabricCanvas.backgroundColor = bgColor;
            fabricCanvas.renderAll();
        } else if (data.fondo.tipo === 'gradiente' && data.fondo.valor) {
            const grad = data.fondo.valor;
            // Usar dimensiones reales (sin zoom), ya que el zoom es CSS
            const gradient = new Gradient({
                type: 'linear',
                coords: {
                    x1: 0,
                    y1: 0,
                    x2: displayWidth,
                    y2: displayHeight
                },
                colorStops: grad.colores.map((c) => ({
                    offset: c.posicion / 100,
                    color: c.color
                }))
            });

            // Ajustar dirección del gradiente según la dirección especificada
            const angle = parseInt(grad.direccion) || 135;
            const radians = (angle * Math.PI) / 180;
            const centerX = displayWidth / 2;
            const centerY = displayHeight / 2;
            const length = Math.sqrt(displayWidth * displayWidth + displayHeight * displayHeight) / 2;

            gradient.coords = {
                x1: centerX - Math.cos(radians) * length,
                y1: centerY - Math.sin(radians) * length,
                x2: centerX + Math.cos(radians) * length,
                y2: centerY + Math.sin(radians) * length
            };

            const bgRect = new Rect({
                left: 0,
                top: 0,
                width: displayWidth,
                height: displayHeight,
                fill: gradient,
                selectable: false,
                evented: false,
                isBackgroundGradient: true
            });
            fabricCanvas.add(bgRect);
            fabricCanvas.sendObjectToBack(bgRect);
            fabricCanvas.renderAll();
        } else if (data.fondo.tipo === 'imagen' && (data.fondo.valor?.cdnUrl || data.fondo.valor?.url)) {
            // Usar dimensiones reales (sin zoom), ya que el zoom es CSS
            const displayWidth = data.dimensiones.ancho;
            const displayHeight = data.dimensiones.alto;

            FabricImage.fromURL(data.fondo.valor.cdnUrl || data.fondo.valor.url, {
                crossOrigin: 'anonymous'
            }).then((img) => {
                const modo = data.fondo.valor.modo || 'cover';
                let scaleX, scaleY, left = 0, top = 0;

                if (modo === 'cover') {
                    const scale = Math.max(displayWidth / img.width, displayHeight / img.height);
                    scaleX = scale;
                    scaleY = scale;
                    left = (displayWidth - img.width * scale) / 2;
                    top = (displayHeight - img.height * scale) / 2;
                } else if (modo === 'contain') {
                    const scale = Math.min(displayWidth / img.width, displayHeight / img.height);
                    scaleX = scale;
                    scaleY = scale;
                    left = (displayWidth - img.width * scale) / 2;
                    top = (displayHeight - img.height * scale) / 2;
                } else { // repeat
                    scaleX = 1;
                    scaleY = 1;
                }

                img.set({
                    left,
                    top,
                    scaleX,
                    scaleY,
                    opacity: data.fondo.valor.opacidad || 1,
                    selectable: false,
                    evented: false,
                    isBackgroundImage: true
                });
                fabricCanvas.add(img);
                fabricCanvas.sendObjectToBack(img);
                fabricCanvas.renderAll();
            }).catch((error) => {
                console.error('Error cargando imagen de fondo:', error);
            });
        }
    }, [fabricCanvas, data?.fondo, currentZoom]);

    const loadElements = (canvas, elementos) => {
        // Verificar qué elementos ya existen en el canvas
        const existingIds = canvas.getObjects()
            .filter(obj => obj.elementId && !obj.isBackgroundImage && !obj.isBackgroundGradient)
            .map(obj => obj.elementId);


        elementos.forEach((elemento) => {
            // Si el elemento ya existe, no cargarlo de nuevo
            if (existingIds.includes(elemento.id)) {
                return;
            }


            let fabricObject = null;

            switch (elemento.tipo) {
                case 'texto':
                    const textoProps = elemento.propiedades || {};
                    const contenido = textoProps.contenido || 'Nuevo texto';
                    const tamaño = textoProps.tamaño || 24;
                    const color = textoProps.color || '#000000';
                    const fuente = textoProps.fuente || 'Arial';


                    try {
                        // Usar dimensiones reales (sin zoom), ya que el zoom es CSS
                        fabricObject = new Text(contenido, {
                            left: elemento.posicion.x,
                            top: elemento.posicion.y,
                            fontSize: tamaño,
                            fill: color,
                            fontFamily: fuente,
                            fontWeight: textoProps.negrita ? 'bold' : 'normal',
                            fontStyle: textoProps.cursiva ? 'italic' : 'normal',
                            textAlign: textoProps.alineacion || 'left',
                            width: elemento.dimensiones?.ancho || 200,
                            selectable: true,
                            hasControls: true,
                            hasBorders: true
                        });

                        // CRÍTICO: Asignar elementId y elementProperties ANTES de añadir al canvas
                        fabricObject.elementId = elemento.id;
                        fabricObject.elementType = 'texto';
                        fabricObject.elementProperties = { ...elemento.propiedades };

                    } catch (error) {
                    }
                    break;

                case 'imagen':
                    const imageUrl = elemento.propiedades.cdnUrl || elemento.propiedades.url;
                    if (imageUrl) {
                        // Intentar cargar la imagen: primero sin crossOrigin (para que se vea),
                        // luego intentar con crossOrigin si es necesario para exportar
                        const loadImage = async () => {
                            try {
                                // Método 1: Intentar sin crossOrigin primero (para que se vea en el canvas)
                                // Esto funciona aunque no se pueda exportar después
                                let img;
                                try {
                                    img = await FabricImage.fromURL(imageUrl);
                                } catch (e) {
                                    // Si falla, intentar con crossOrigin
                                    img = await FabricImage.fromURL(imageUrl, {
                                        crossOrigin: 'anonymous'
                                    });
                                }

                                // Usar dimensiones reales (sin zoom), ya que el zoom es CSS
                                const scaledWidth = elemento.dimensiones.ancho;
                                const scaledHeight = elemento.dimensiones.alto;

                                // Calcular escala para mantener proporciones
                                const scaleX = scaledWidth / img.width;
                                const scaleY = scaledHeight / img.height;

                                img.set({
                                    left: elemento.posicion.x,
                                    top: elemento.posicion.y,
                                    scaleX: scaleX,
                                    scaleY: scaleY,
                                    selectable: true,
                                    hasControls: true,
                                    hasBorders: true,
                                    opacity: elemento.propiedades.opacidad || 1
                                });
                                img.elementId = elemento.id;
                                img.elementType = 'imagen';
                                img.elementProperties = elemento.propiedades;

                                // Marcar si la imagen tiene CORS o no (para exportación)
                                img.hasCORS = false; // Se usará el proxy al exportar

                                // Eliminar cualquier placeholder existente
                                const existingObjects = canvas.getObjects().filter(o => o.elementId === elemento.id);
                                existingObjects.forEach(o => canvas.remove(o));

                                canvas.add(img);
                                canvas.renderAll();

                                // Nota: No intentamos cargar con CORS aquí, el proxy se encarga al exportar
                            } catch (error) {
                                // Crear placeholder si falla la carga
                                const existingObjects = canvas.getObjects().filter(o => o.elementId === elemento.id);
                                existingObjects.forEach(o => canvas.remove(o));

                                const placeholder = new Rect({
                                    left: elemento.posicion.x,
                                    top: elemento.posicion.y,
                                    width: elemento.dimensiones.ancho,
                                    height: elemento.dimensiones.alto,
                                    fill: '#E5E7EB',
                                    stroke: '#9CA3AF',
                                    strokeWidth: 2,
                                    strokeDashArray: [5, 5],
                                    selectable: true,
                                    hasControls: true,
                                    hasBorders: true
                                });
                                placeholder.elementId = elemento.id;
                                placeholder.elementType = 'imagen';
                                placeholder.elementProperties = elemento.propiedades;
                                canvas.add(placeholder);
                                canvas.renderAll();
                            }
                        };

                        loadImage();
                        return;
                    } else {
                        // Si no hay URL, crear un placeholder visual
                        const placeholder = new Rect({
                            left: elemento.posicion.x,
                            top: elemento.posicion.y,
                            width: elemento.dimensiones.ancho,
                            height: elemento.dimensiones.alto,
                            fill: '#F3F4F6',
                            stroke: '#D1D5DB',
                            strokeWidth: 2,
                            strokeDashArray: [5, 5],
                            selectable: true,
                            hasControls: true,
                            hasBorders: true
                        });
                        placeholder.elementId = elemento.id;
                        placeholder.elementType = 'imagen';
                        placeholder.elementProperties = elemento.propiedades;
                        canvas.add(placeholder);
                    }
                    break;

                case 'qr':
                    // Generar QR real usando la librería qrcode
                    const qrUrl = elemento.propiedades?.url || 'https://example.com';
                    const qrColor = elemento.propiedades?.color || '#000000';
                    const qrBgColor = elemento.propiedades?.colorFondo || '#FFFFFF';
                    const qrSize = elemento.propiedades?.tamaño || elemento.dimensiones.ancho || 300;

                    QRCode.toDataURL(qrUrl, {
                        width: qrSize,
                        margin: 2,
                        color: {
                            dark: qrColor,
                            light: qrBgColor
                        },
                        errorCorrectionLevel: 'H'
                    })
                        .then((dataUrl) => {
                            // Crear imagen desde el data URL del QR
                            FabricImage.fromURL(dataUrl, {
                                crossOrigin: 'anonymous'
                            })
                                .then((img) => {
                                    // Usar dimensiones reales (sin zoom), ya que el zoom es CSS
                                    const scaledWidth = elemento.dimensiones.ancho;
                                    const scaledHeight = elemento.dimensiones.alto;

                                    // Calcular escala para mantener proporciones
                                    const scaleX = scaledWidth / img.width;
                                    const scaleY = scaledHeight / img.height;

                                    img.set({
                                        left: elemento.posicion.x,
                                        top: elemento.posicion.y,
                                        scaleX: scaleX,
                                        scaleY: scaleY,
                                        selectable: true,
                                        hasControls: true,
                                        hasBorders: true
                                    });
                                    img.elementId = elemento.id;
                                    img.elementType = 'qr';
                                    img.elementProperties = elemento.propiedades;
                                    canvas.add(img);
                                    canvas.renderAll();
                                })
                                .catch((error) => {
                                    // Crear placeholder si falla
                                    const placeholder = new Rect({
                                        left: elemento.posicion.x,
                                        top: elemento.posicion.y,
                                        width: elemento.dimensiones.ancho,
                                        height: elemento.dimensiones.alto,
                                        fill: '#F3F4F6',
                                        stroke: '#D1D5DB',
                                        strokeWidth: 2,
                                        strokeDashArray: [5, 5],
                                        selectable: true,
                                        hasControls: true,
                                        hasBorders: true
                                    });
                                    placeholder.elementId = elemento.id;
                                    placeholder.elementType = 'qr';
                                    placeholder.elementProperties = elemento.propiedades;
                                    canvas.add(placeholder);
                                    canvas.renderAll();
                                });
                        })
                        .catch((error) => {
                            // Crear placeholder si falla la generación
                            const placeholder = new Rect({
                                left: elemento.posicion.x,
                                top: elemento.posicion.y,
                                width: elemento.dimensiones.ancho,
                                height: elemento.dimensiones.alto,
                                fill: '#F3F4F6',
                                stroke: '#D1D5DB',
                                strokeWidth: 2,
                                strokeDashArray: [5, 5],
                                selectable: true,
                                hasControls: true,
                                hasBorders: true
                            });
                            placeholder.elementId = elemento.id;
                            placeholder.elementType = 'qr';
                            placeholder.elementProperties = elemento.propiedades;
                            canvas.add(placeholder);
                            canvas.renderAll();
                        });
                    // No crear objeto aquí, se crea en el .then()
                    return;

                case 'icono':
                    // Renderizar icono como SVG usando un canvas temporal
                    const iconProps = elemento.propiedades || {};
                    const iconName = iconProps.nombre;
                    const iconLibrary = iconProps.libreria;
                    const iconColor = iconProps.color || '#000000';
                    const iconSize = iconProps.tamaño || elemento.dimensiones.ancho || 100;
                    const iconOpacity = iconProps.opacidad || 1;

                    if (iconName && iconLibrary) {
                        // Crear un canvas temporal para renderizar el icono
                        const tempCanvas = document.createElement('canvas');
                        tempCanvas.width = iconSize;
                        tempCanvas.height = iconSize;
                        const ctx = tempCanvas.getContext('2d');

                        // Crear un div temporal fuera del DOM para renderizar React
                        const tempDiv = document.createElement('div');
                        tempDiv.style.position = 'absolute';
                        tempDiv.style.left = '-9999px';
                        tempDiv.style.width = `${iconSize}px`;
                        tempDiv.style.height = `${iconSize}px`;
                        document.body.appendChild(tempDiv);

                        try {
                            // Importar la librería de iconos correspondiente
                            let IconComponent = null;
                            let IconLib = null;

                            if (iconLibrary === 'Font Awesome') {
                                IconLib = require('react-icons/fa');
                            } else if (iconLibrary === 'Material Design') {
                                IconLib = require('react-icons/md');
                            } else if (iconLibrary === 'Heroicons') {
                                IconLib = require('react-icons/hi');
                            } else if (iconLibrary === 'Ionicons') {
                                IconLib = require('react-icons/io5');
                            }

                            if (IconLib && IconLib[iconName]) {
                                IconComponent = IconLib[iconName];
                            }

                            if (IconComponent && ctx) {
                                // Usar React para renderizar el icono
                                const React = require('react');
                                const ReactDOM = require('react-dom/client');
                                const root = ReactDOM.createRoot(tempDiv);

                                root.render(React.createElement(IconComponent, {
                                    style: {
                                        width: `${iconSize}px`,
                                        height: `${iconSize}px`,
                                        color: iconColor,
                                        opacity: iconOpacity
                                    }
                                }));

                                // Esperar a que React renderice el SVG
                                setTimeout(() => {
                                    const svgElement = tempDiv.querySelector('svg');
                                    if (svgElement) {
                                        // Asegurar que el SVG tenga el tamaño y color correctos
                                        svgElement.setAttribute('width', iconSize.toString());
                                        svgElement.setAttribute('height', iconSize.toString());
                                        svgElement.setAttribute('fill', iconColor);
                                        svgElement.style.opacity = iconOpacity.toString();

                                        // Convertir SVG a data URL
                                        const svgString = new XMLSerializer().serializeToString(svgElement);
                                        const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
                                        const svgUrl = URL.createObjectURL(svgBlob);

                                        FabricImage.fromURL(svgUrl, {
                                            crossOrigin: 'anonymous'
                                        })
                                            .then((img) => {
                                                const scaledWidth = elemento.dimensiones.ancho;
                                                const scaledHeight = elemento.dimensiones.alto;
                                                const scaleX = scaledWidth / img.width;
                                                const scaleY = scaledHeight / img.height;

                                                img.set({
                                                    left: elemento.posicion.x,
                                                    top: elemento.posicion.y,
                                                    scaleX: scaleX,
                                                    scaleY: scaleY,
                                                    opacity: iconOpacity,
                                                    selectable: true,
                                                    hasControls: true,
                                                    hasBorders: true
                                                });
                                                img.elementId = elemento.id;
                                                img.elementType = 'icono';
                                                img.elementProperties = elemento.propiedades;
                                                canvas.add(img);
                                                canvas.renderAll();
                                                URL.revokeObjectURL(svgUrl);
                                                document.body.removeChild(tempDiv);
                                            })
                                            .catch((error) => {
                                                document.body.removeChild(tempDiv);
                                                // Crear placeholder si falla
                                                const placeholder = new Rect({
                                                    left: elemento.posicion.x,
                                                    top: elemento.posicion.y,
                                                    width: elemento.dimensiones.ancho,
                                                    height: elemento.dimensiones.alto,
                                                    fill: '#F3F4F6',
                                                    stroke: '#D1D5DB',
                                                    strokeWidth: 2,
                                                    strokeDashArray: [5, 5],
                                                    selectable: true,
                                                    hasControls: true,
                                                    hasBorders: true
                                                });
                                                placeholder.elementId = elemento.id;
                                                placeholder.elementType = 'icono';
                                                placeholder.elementProperties = elemento.propiedades;
                                                canvas.add(placeholder);
                                                canvas.renderAll();
                                            });
                                    } else {
                                        document.body.removeChild(tempDiv);
                                        // Crear placeholder si no se encuentra el SVG
                                        const placeholder = new Rect({
                                            left: elemento.posicion.x,
                                            top: elemento.posicion.y,
                                            width: elemento.dimensiones.ancho,
                                            height: elemento.dimensiones.alto,
                                            fill: '#F3F4F6',
                                            stroke: '#D1D5DB',
                                            strokeWidth: 2,
                                            strokeDashArray: [5, 5],
                                            selectable: true,
                                            hasControls: true,
                                            hasBorders: true
                                        });
                                        placeholder.elementId = elemento.id;
                                        placeholder.elementType = 'icono';
                                        placeholder.elementProperties = elemento.propiedades;
                                        canvas.add(placeholder);
                                        canvas.renderAll();
                                    }
                                }, 200);
                                return; // No crear objeto aquí, se crea en el .then()
                            } else {
                                document.body.removeChild(tempDiv);
                            }
                        } catch (error) {
                            if (tempDiv.parentNode) {
                                document.body.removeChild(tempDiv);
                            }
                        }
                    }
                    // Si no hay icono válido, crear placeholder
                    fabricObject = new Rect({
                        left: elemento.posicion.x,
                        top: elemento.posicion.y,
                        width: elemento.dimensiones.ancho,
                        height: elemento.dimensiones.alto,
                        fill: '#F3F4F6',
                        stroke: '#D1D5DB',
                        strokeWidth: 2,
                        strokeDashArray: [5, 5],
                        selectable: true,
                        hasControls: true,
                        hasBorders: true
                    });
                    // Asegurar que el placeholder tenga el elementId correcto
                    if (fabricObject) {
                        fabricObject.elementId = elemento.id;
                        fabricObject.elementType = 'icono';
                        fabricObject.elementProperties = elemento.propiedades || {};
                    }
                    break;

                case 'forma':
                    const shapeType = elemento.propiedades.tipo;
                    const shapeProps = {
                        left: elemento.posicion.x,
                        top: elemento.posicion.y,
                        fill: elemento.propiedades.colorRelleno || '#3B82F6',
                        stroke: elemento.propiedades.colorBorde || '#000000',
                        strokeWidth: elemento.propiedades.grosorBorde || 0,
                        opacity: elemento.propiedades.opacidad || 1,
                        selectable: true,
                        hasControls: true,
                        hasBorders: true
                    };

                    switch (shapeType) {
                        case 'rectangulo':
                            fabricObject = new Rect({
                                ...shapeProps,
                                width: elemento.dimensiones.ancho,
                                height: elemento.dimensiones.alto
                            });
                            break;

                        case 'cuadrado':
                            const sideLength = Math.max(elemento.dimensiones.ancho, elemento.dimensiones.alto);
                            fabricObject = new Rect({
                                ...shapeProps,
                                width: sideLength,
                                height: sideLength
                            });
                            break;

                        case 'circulo':
                            fabricObject = new Circle({
                                ...shapeProps,
                                radius: elemento.dimensiones.ancho / 2
                            });
                            break;

                        case 'elipse':
                            fabricObject = new Ellipse({
                                ...shapeProps,
                                rx: elemento.dimensiones.ancho / 2,
                                ry: elemento.dimensiones.alto / 2
                            });
                            break;

                        case 'triangulo':
                            fabricObject = new Triangle({
                                ...shapeProps,
                                width: elemento.dimensiones.ancho,
                                height: elemento.dimensiones.alto
                            });
                            break;

                        case 'triangulo-equilatero':
                            const triSize = Math.max(elemento.dimensiones.ancho, elemento.dimensiones.alto);
                            const triPoints = generateEquilateralTriangle(triSize);
                            fabricObject = new Polygon(triPoints, {
                                ...shapeProps,
                                objectCaching: false
                            });
                            break;

                        case 'rombo':
                            const diamondPoints = generateDiamond(elemento.dimensiones.ancho, elemento.dimensiones.alto);
                            fabricObject = new Polygon(diamondPoints, {
                                ...shapeProps,
                                objectCaching: false
                            });
                            break;

                        case 'paralelogramo':
                            const paraPoints = generateParallelogram(elemento.dimensiones.ancho, elemento.dimensiones.alto);
                            fabricObject = new Polygon(paraPoints, {
                                ...shapeProps,
                                objectCaching: false
                            });
                            break;

                        case 'trapecio':
                            const trapPoints = generateTrapezoid(elemento.dimensiones.ancho, elemento.dimensiones.alto);
                            fabricObject = new Polygon(trapPoints, {
                                ...shapeProps,
                                objectCaching: false
                            });
                            break;

                        case 'pentagono':
                            const pentaRadius = Math.max(elemento.dimensiones.ancho, elemento.dimensiones.alto) / 2;
                            const pentaPoints = generateRegularPolygon(5, pentaRadius);
                            fabricObject = new Polygon(pentaPoints, {
                                ...shapeProps,
                                objectCaching: false
                            });
                            break;

                        case 'hexagono':
                            const hexaRadius = Math.max(elemento.dimensiones.ancho, elemento.dimensiones.alto) / 2;
                            const hexaPoints = generateRegularPolygon(6, hexaRadius);
                            fabricObject = new Polygon(hexaPoints, {
                                ...shapeProps,
                                objectCaching: false
                            });
                            break;

                        case 'heptagono':
                            const heptaRadius = Math.max(elemento.dimensiones.ancho, elemento.dimensiones.alto) / 2;
                            const heptaPoints = generateRegularPolygon(7, heptaRadius);
                            fabricObject = new Polygon(heptaPoints, {
                                ...shapeProps,
                                objectCaching: false
                            });
                            break;

                        case 'octagono':
                            const octaRadius = Math.max(elemento.dimensiones.ancho, elemento.dimensiones.alto) / 2;
                            const octaPoints = generateRegularPolygon(8, octaRadius);
                            fabricObject = new Polygon(octaPoints, {
                                ...shapeProps,
                                objectCaching: false
                            });
                            break;

                        case 'nonagono':
                            const nonaRadius = Math.max(elemento.dimensiones.ancho, elemento.dimensiones.alto) / 2;
                            const nonaPoints = generateRegularPolygon(9, nonaRadius);
                            fabricObject = new Polygon(nonaPoints, {
                                ...shapeProps,
                                objectCaching: false
                            });
                            break;

                        case 'decagono':
                            const decaRadius = Math.max(elemento.dimensiones.ancho, elemento.dimensiones.alto) / 2;
                            const decaPoints = generateRegularPolygon(10, decaRadius);
                            fabricObject = new Polygon(decaPoints, {
                                ...shapeProps,
                                objectCaching: false
                            });
                            break;

                        case 'estrella':
                            const starRadius = Math.max(elemento.dimensiones.ancho, elemento.dimensiones.alto) / 2;
                            const starPoints = generateStar(5, starRadius, starRadius * 0.5);
                            fabricObject = new Polygon(starPoints, {
                                ...shapeProps,
                                objectCaching: false
                            });
                            break;

                        case 'flecha-derecha':
                            const arrowWidth = elemento.dimensiones.ancho;
                            const arrowHeight = elemento.dimensiones.alto;
                            const arrowPoints = [
                                { x: -arrowWidth * 0.3, y: -arrowHeight * 0.3 },
                                { x: arrowWidth * 0.1, y: -arrowHeight * 0.3 },
                                { x: arrowWidth * 0.1, y: -arrowHeight * 0.5 },
                                { x: arrowWidth * 0.5, y: 0 },
                                { x: arrowWidth * 0.1, y: arrowHeight * 0.5 },
                                { x: arrowWidth * 0.1, y: arrowHeight * 0.3 },
                                { x: -arrowWidth * 0.3, y: arrowHeight * 0.3 }
                            ];
                            fabricObject = new Polygon(arrowPoints, {
                                ...shapeProps,
                                objectCaching: false
                            });
                            break;

                        case 'flecha-izquierda':
                            const arrowLWidth = elemento.dimensiones.ancho;
                            const arrowLHeight = elemento.dimensiones.alto;
                            const arrowLPoints = [
                                { x: arrowLWidth * 0.3, y: -arrowLHeight * 0.3 },
                                { x: -arrowLWidth * 0.1, y: -arrowLHeight * 0.3 },
                                { x: -arrowLWidth * 0.1, y: -arrowLHeight * 0.5 },
                                { x: -arrowLWidth * 0.5, y: 0 },
                                { x: -arrowLWidth * 0.1, y: arrowLHeight * 0.5 },
                                { x: -arrowLWidth * 0.1, y: arrowLHeight * 0.3 },
                                { x: arrowLWidth * 0.3, y: arrowLHeight * 0.3 }
                            ];
                            fabricObject = new Polygon(arrowLPoints, {
                                ...shapeProps,
                                objectCaching: false
                            });
                            break;

                        case 'corazon':
                            const heartSize = Math.max(elemento.dimensiones.ancho, elemento.dimensiones.alto);
                            const heartPath = `M ${heartSize * 0.5},${heartSize * 0.3} 
                                C ${heartSize * 0.5},${heartSize * 0.2} ${heartSize * 0.4},${heartSize * 0.1} ${heartSize * 0.3},${heartSize * 0.1} 
                                C ${heartSize * 0.15},${heartSize * 0.1} ${heartSize * 0.1},${heartSize * 0.2} ${heartSize * 0.1},${heartSize * 0.3} 
                                C ${heartSize * 0.1},${heartSize * 0.45} ${heartSize * 0.2},${heartSize * 0.55} ${heartSize * 0.5},${heartSize * 0.8} 
                                C ${heartSize * 0.8},${heartSize * 0.55} ${heartSize * 0.9},${heartSize * 0.45} ${heartSize * 0.9},${heartSize * 0.3} 
                                C ${heartSize * 0.9},${heartSize * 0.2} ${heartSize * 0.85},${heartSize * 0.1} ${heartSize * 0.7},${heartSize * 0.1} 
                                C ${heartSize * 0.6},${heartSize * 0.1} ${heartSize * 0.5},${heartSize * 0.2} ${heartSize * 0.5},${heartSize * 0.3} Z`;
                            fabricObject = new Path(heartPath, {
                                ...shapeProps,
                                objectCaching: false
                            });
                            break;

                        case 'cruz':
                            const cruzSize = Math.max(elemento.dimensiones.ancho, elemento.dimensiones.alto);
                            const cruzThickness = cruzSize * 0.3;
                            const cruzPoints = [
                                { x: -cruzThickness / 2, y: -cruzSize / 2 },
                                { x: cruzThickness / 2, y: -cruzSize / 2 },
                                { x: cruzThickness / 2, y: -cruzThickness / 2 },
                                { x: cruzSize / 2, y: -cruzThickness / 2 },
                                { x: cruzSize / 2, y: cruzThickness / 2 },
                                { x: cruzThickness / 2, y: cruzThickness / 2 },
                                { x: cruzThickness / 2, y: cruzSize / 2 },
                                { x: -cruzThickness / 2, y: cruzSize / 2 },
                                { x: -cruzThickness / 2, y: cruzThickness / 2 },
                                { x: -cruzSize / 2, y: cruzThickness / 2 },
                                { x: -cruzSize / 2, y: -cruzThickness / 2 },
                                { x: -cruzThickness / 2, y: -cruzThickness / 2 }
                            ];
                            fabricObject = new Polygon(cruzPoints, {
                                ...shapeProps,
                                objectCaching: false
                            });
                            break;

                        default:
                            // Default a rectángulo si no se reconoce el tipo
                            fabricObject = new Rect({
                                ...shapeProps,
                                width: elemento.dimensiones.ancho,
                                height: elemento.dimensiones.alto
                            });
                    }
                    break;

                default:
                    return;
            }

            if (fabricObject) {
                fabricObject.elementId = elemento.id;
                fabricObject.elementType = elemento.tipo;
                fabricObject.elementProperties = elemento.propiedades || {};
                fabricObject.angle = elemento.rotacion || 0;

                canvas.add(fabricObject);

                // El zIndex se manejará después de que todos los objetos se añadan
                // mediante el useEffect de sincronización
            } else {
            }
        });

        canvas.renderAll();
    };

    if (!data) {
        return (
            <div className="flex justify-center items-center min-h-full w-full h-full">
                <p className="text-gray-500">Cargando canvas...</p>
            </div>
        );
    }

    const scaledWidth = data.dimensiones.ancho * currentZoom;
    const scaledHeight = data.dimensiones.alto * currentZoom;

    return (
        <div
            ref={containerRef}
            className="w-full h-full flex items-center justify-center p-8"
            style={{
                minWidth: `${scaledWidth + 64}px`,
                minHeight: `${scaledHeight + 64}px`
            }}
        >
            <div
                style={{
                    width: `${scaledWidth}px`,
                    height: `${scaledHeight}px`,
                    position: 'relative'
                }}
            >
                <canvas
                    ref={canvasRef}
                    style={{
                        display: 'block',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    }}
                />
            </div>
        </div>
    );
};

export default InfografiaCanvas;
